import Close from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button, CircularProgress, DialogContent,
  Divider,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetSessionDetailsQuery } from '../../../../../generated/core-api/graphql-react-query';
import SessionInfo from '../../../../Components/MainWindow/Components/Pages/SessionsPage/Components/SessionDetailsPage/Components/SessionInfo';
import SessionNote from '../../../../Components/MainWindow/Components/Pages/SessionsPage/Components/SessionDetailsPage/Components/SessionNote';
import SessionSummary from '../../../../Components/MainWindow/Components/Pages/SessionsPage/Components/SessionDetailsPage/Components/SessionSummary';
import SummaryDisplay from '../../../../Components/MainWindow/Components/Pages/SessionsPage/Components/SessionDetailsPage/Components/SummaryDisplay';
import SummaryRequestContent from '../../../../Components/MainWindow/Components/Pages/SessionsPage/Components/SessionDetailsPage/Components/SummaryRequestContent';
import StyledSnackbarProvider from '../../../../Components/StyledSnackbarProvider';
import PageContentLayout from '../../../../Layouts/PageContentLayout';
import PageHeader from '../../../../Layouts/PageHeader';
import StyledDialog, { StyledLayout } from './styles';

interface EndSessionDialogProps {
  open: string | null;
  onClose: () => void;
}

function Layout(props: { children: React.ReactNode, sessionId: string | null, onClose: () => void }) {
  const { sessionId, onClose } = props;
  const { t } = useTranslation('session');
  const { children } = props;
  const { t: tCommon } = useTranslation('common');

  return (
    <StyledDialog open={!!sessionId} onClose={onClose} fullWidth maxWidth="sm">
      {sessionId && (
        <StyledSnackbarProvider>
          <DialogContent>
            <Button onClick={onClose} title={tCommon('close')}>
              <Close />
            </Button>
            <StyledLayout id="session-details-dialog">
              <PageHeader title={t('details.title')} />
              <PageContentLayout className="content" inDialog>
                {children}
              </PageContentLayout>
            </StyledLayout>
          </DialogContent>
        </StyledSnackbarProvider>
      )}
    </StyledDialog>
  );
}

export default function EndSessionDialog(props: EndSessionDialogProps) {
  const { open: sessionId, onClose } = props;
  const session = useGetSessionDetailsQuery({ input: { id: sessionId ?? '' } }, { enabled: !!sessionId });
  if (session.isLoading) {
    return (
      <Layout onClose={onClose} sessionId={sessionId}>
        <CircularProgress />
      </Layout>
    );
  }

  if (session.isError) {
    return (
      <Layout onClose={onClose} sessionId={sessionId}>
        <Alert severity="error">{(session.error as Error).message}</Alert>
      </Layout>
    );
  }

  if (!session.data) { return null; }
  return (
    <Layout sessionId={sessionId} onClose={onClose}>
      <SessionInfo session={session.data.getSession} />
      <Box className="info">
        <SessionSummary session={session.data.getSession} />
        <SessionNote session={session.data.getSession} />
        <SummaryDisplay displays={session.data.getSession.displays ?? []} />
      </Box>
      <Divider />
      <SummaryRequestContent />
    </Layout>

  );
}
